import { useTranslation } from 'react-i18next';
import { ROLE_TEMPLATES, type RoleTemplateId } from '@/shared/permissions/roleTemplates';
import { Icon } from '@/presentation/components/common/Icon';

export type RoleTemplateSelection = RoleTemplateId | 'custom';

const ICONS: Record<RoleTemplateSelection, string> = {
  custom: 'M12 4v16m8-8H4',
  readOnly: 'M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  financeOnly: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
  clinicOps: 'M3 21V7l9-4 9 4v14M9 21V12h6v9',
};

export function RoleTemplatePicker({
  selectedId,
  onSelect,
  showCustom = true,
  hideHint = false,
}: {
  selectedId: RoleTemplateSelection | null;
  onSelect: (id: RoleTemplateSelection) => void;
  showCustom?: boolean;
  hideHint?: boolean;
}) {
  const { t } = useTranslation();
  const customSelected = selectedId === 'custom' || selectedId === null;

  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('permissions.templates.title')}</p>
        {!hideHint && (
          <p className="mt-0.5 text-xs leading-5 text-gray-500 dark:text-gray-400">{t('permissions.templates.hint')}</p>
        )}
      </div>
      <div className="grid auto-rows-fr grid-cols-1 gap-2 sm:grid-cols-2">
        {showCustom && (
          <TemplateCard
            testId="role-template-custom"
            selected={customSelected}
            icon={ICONS.custom}
            title={t('permissions.templates.custom')}
            description={t('permissions.templates.customHint')}
            onClick={() => onSelect('custom')}
          />
        )}
        {ROLE_TEMPLATES.map((template) => (
          <TemplateCard
            key={template.id}
            testId={`role-template-${template.id}`}
            selected={selectedId === template.id}
            icon={ICONS[template.id]}
            title={t(template.nameKey)}
            description={t(template.descriptionKey)}
            onClick={() => onSelect(template.id)}
          />
        ))}
      </div>
    </div>
  );
}

function TemplateCard({
  testId,
  selected,
  icon,
  title,
  description,
  onClick,
}: {
  testId: string;
  selected: boolean;
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={selected}
      onClick={onClick}
      className={[
        'flex h-full min-h-[4.75rem] items-start gap-3 rounded-xl border p-3 text-left transition',
        selected
          ? 'border-teal-500 bg-teal-50 shadow-[inset_0_0_0_1px] shadow-teal-500 dark:border-teal-400 dark:bg-teal-950/40 dark:shadow-teal-400'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/60 dark:hover:border-gray-600 dark:hover:bg-gray-900',
      ].join(' ')}
    >
      <span
        className={[
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          selected
            ? 'bg-teal-600 text-white dark:bg-teal-500'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
        ].join(' ')}
      >
        <Icon path={icon} className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-5 text-gray-900 dark:text-white">{title}</span>
        <span className="mt-0.5 block text-xs leading-5 text-gray-500 dark:text-gray-400">{description}</span>
      </span>
    </button>
  );
}
